import { useCallback, useMemo } from 'react';
import { useWorkflowStore } from '@/state/workflow.state';
import { NodeType } from '@/enums/node-type.enum';
import { I18N_KEYS } from '@/language/keys';
import { PACK_UNPACK_EVENT_NAMES } from '@/consts/analytics.consts';
import { useAnalytics, TrackTypeEnum } from '@/hooks/useAnalytics';
import { useFlowView } from '../Recipe/FlowContext';
import { createNewNode } from '../Recipe/useGraph';
import { PACK_UNPACK_GAP } from './packUnpack.consts';
import {
  getImportNodeFileType,
  getRightmostX,
  getAverageY,
  getIteratorInfo,
  nodesToPackUnpackItems,
} from './packUnpack.utils';
import type { PackableFileType } from './packUnpack.consts';
import type { NodeId } from 'web';
import type { Node } from '@/types/node';

const PACK_LABEL_KEY_BY_FILE_TYPE: Record<PackableFileType, string> = {
  image: I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.CREATE_IMAGE_ITERATOR,
  video: I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.CREATE_VIDEO_ITERATOR,
};

export interface PackAnalysis {
  canPack: boolean;
  labelKey: string;
}

export function usePackNodes() {
  const flowView = useFlowView();
  const nodeTypes = useWorkflowStore((state) => state.nodeTypes);
  const { track } = useAnalytics();

  const analyzeSelection = useCallback(
    (selectedNodeIds: NodeId[]): PackAnalysis => {
      if (selectedNodeIds.length < 2) return { canPack: false, labelKey: '' };

      const nodes = selectedNodeIds.map((id) => flowView.getNodeNullable(id)).filter((n): n is Node => n !== undefined);
      if (nodes.length < 2) return { canPack: false, labelKey: '' };

      const allImport = nodes.every((n) => n.type === NodeType.Import);
      if (allImport) {
        const fileTypes = nodes.map(getImportNodeFileType);
        const firstType = fileTypes[0];
        if (firstType && fileTypes.every((t) => t === firstType)) {
          return { canPack: true, labelKey: PACK_LABEL_KEY_BY_FILE_TYPE[firstType] };
        }
        return { canPack: false, labelKey: '' };
      }

      const allPrompt = nodes.every((n) => n.type === NodeType.PromptV3 || n.type === NodeType.Prompt);
      if (allPrompt) {
        return { canPack: true, labelKey: I18N_KEYS.RECIPE_MAIN.SELECTION_CONTEXT_MENU.CREATE_TEXT_ITERATOR };
      }

      return { canPack: false, labelKey: '' };
    },
    [flowView],
  );

  const packNodes = useCallback(
    (selectedNodeIds: NodeId[]) => {
      const nodes = selectedNodeIds.map((id) => flowView.getNodeNullable(id)).filter((n): n is Node => n !== undefined);
      if (nodes.length < 2) return;

      const items = nodesToPackUnpackItems(nodes);
      if (items.length === 0) return;

      const info = getIteratorInfo(items);
      if (!info) return;

      const position = {
        x: getRightmostX(nodes) + PACK_UNPACK_GAP,
        y: getAverageY(nodes),
      };

      const newNode = createNewNode(info.iteratorId, nodeTypes, position, info.data);
      if (newNode) {
        flowView.addNewNode(newNode);
        track(PACK_UNPACK_EVENT_NAMES.CREATE_ITERATOR_FROM_GROUP, { itemCount: items.length }, TrackTypeEnum.Product);
      }
    },
    [flowView, nodeTypes, track],
  );

  return { analyzeSelection, packNodes };
}

export function usePackAnalysis(selectedNodeIds: NodeId[]) {
  const { analyzeSelection, packNodes } = usePackNodes();

  const analysis = useMemo(() => analyzeSelection(selectedNodeIds), [analyzeSelection, selectedNodeIds]);

  const handlePack = useCallback(() => {
    packNodes(selectedNodeIds);
  }, [packNodes, selectedNodeIds]);

  return { ...analysis, handlePack };
}
